import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Check, Sparkles, Users, FileSpreadsheet, Gavel, Trophy, Truck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { AlertSettingsWidget } from "@/components/dashboard/AlertSettingsWidget";
import {
  useBrokerGettingStartedProgress,
  type BrokerGettingStartedProgress,
} from "@/hooks/useBrokerGettingStartedProgress";

type StepKey = keyof BrokerGettingStartedProgress;

type Step = {
  id: StepKey;
  title: string;
  description: string;
  route: string;
  icon: React.ComponentType<{ className?: string }>;
};

const STEPS: Step[] = [
  {
    id: "network",
    title: "Join a Shipper Network",
    description: "Accept a shipper's invite to bid on their private direct loads.",
    route: "/lr-direct/network-invitations",
    icon: Users,
  },
  {
    id: "creditAppTpl",
    title: "Upload Your Credit App",
    description: "Upload your credit app template so shippers get it instantly when they need it.",
    route: "/company-settings?tab=broker-credit-app",
    icon: FileSpreadsheet,
  },
  {
    id: "firstBid",
    title: "Place Your First Bid",
    description: "Browse the LR Direct board and submit a bid on a shipper's load.",
    route: "/lr-direct/board",
    icon: Gavel,
  },
  {
    id: "firstWin",
    title: "Win a Load",
    description: "Get awarded and see it move into your dispatch queue.",
    route: "/lr-direct/my-wins",
    icon: Trophy,
  },
  {
    id: "dispatchTouch",
    title: "Dispatch a Win",
    description: "Open your won load, add a tracking link, and move it to delivery.",
    route: "/lr-direct/my-wins",
    icon: Truck,
  },
];

export function BrokerGettingStarted() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { progress, loaded } = useBrokerGettingStartedProgress(user?.id);

  const [collapsed, setCollapsed] = useState(false);
  const userToggledRef = useRef(false);
  const autoCollapsedRef = useRef(false);

  const completeCount = STEPS.filter((s) => progress[s.id]).length;
  const allComplete = loaded && completeCount === STEPS.length;

  useEffect(() => {
    if (allComplete && !autoCollapsedRef.current && !userToggledRef.current) {
      autoCollapsedRef.current = true;
      setCollapsed(true);
    }
  }, [allComplete]);

  const handleToggle = () => {
    userToggledRef.current = true;
    setCollapsed((c) => !c);
  };

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={!collapsed}
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10 shrink-0">
          <Sparkles className="h-4 w-4 text-brand-teal" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-teal-deep">
            Getting Started
          </div>
          <div className="text-sm font-semibold text-foreground">
            {completeCount} of {STEPS.length} steps complete
          </div>
        </div>
        {collapsed ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-5 w-5 text-muted-foreground" />
        )}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          <p className="text-xs text-muted-foreground mb-3">
            Get set up in 5 steps. Click any card to jump in.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const complete = progress[step.id];
              return (
                <div
                  key={step.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(step.route)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(step.route);
                    }
                  }}
                  className={cn(
                    "group relative flex flex-col rounded-lg border bg-card p-3 text-left transition-all cursor-pointer",
                    "hover:border-brand-teal hover:shadow-md",
                    complete && "border-brand-teal/40 bg-brand-teal/5",
                  )}
                >
                  <div
                    className={cn(
                      "absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full border",
                      complete
                        ? "bg-brand-teal border-brand-teal text-white"
                        : "bg-background border-muted-foreground/30 text-transparent",
                    )}
                    aria-label={complete ? "Complete" : "Not complete"}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-teal/10 text-brand-teal shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Step {idx + 1}
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-foreground mb-1 pr-6">{step.title}</h3>
                  <p className="text-xs text-muted-foreground leading-snug flex-1">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-border/60">
            <AlertSettingsWidget />
          </div>
        </div>
      )}
    </Card>
  );
}

export default BrokerGettingStarted;
